import { styled } from '@linaria/react';
import { Box, Stack, Text } from '@mantine/core';
import { Fragment, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { TransformedTarget } from '../../../../api/targets-client/targets-client.type';
import { DEFAULT_COLORS } from '../../../../components/allocation-of-costs/allocation-of-costs';
import { newCOLORS } from '../../../../styles/colors';
import { baseWhite, inkLight } from '../../../../styles/design-tokens';
import type { MeasureMetadata, WorkPeriod } from '../../work-periods.type';
import { getUnitsLabel, sortWorkPeriodsByStartDate } from '../helpers/measure-comparison-chart.helpers';
import { buildTrendDatasets, calculateYDomain, transformMeasureData } from '../helpers/measures-over-time.helpers';

const CustomTooltip = (props: any) => {
  const { t } = useTranslation();
  const { active, payload, label, selectedMeasure } = props as any;
  if (active && payload && payload.length) {
    return (
      <Box bg={baseWhite} p={10} style={{ border: `1px solid ${inkLight}` }}>
        <Stack gap={4}>
          <Text>{label}</Text>
          {payload
            .filter((entry: any) => !entry.dataKey.includes('_trend'))
            .map((entry: any) => {
              const color = entry.color;
              const value =
                typeof entry.value === 'number' && entry.value !== null
                  ? entry.value.toFixed(selectedMeasure?.measure_units === 'Percentage' ? 1 : 2)
                  : 'N/A';
              const unit = getUnitsLabel(selectedMeasure?.measure_units || '', t);

              return (
                <Text key={`${entry.dataKey}-${entry.name}`} c={color}>
                  {`${entry.name}: ${value} ${unit}`}
                </Text>
              );
            })}
        </Stack>
      </Box>
    );
  }
  return null;
};

interface MeasuresOverTimeChartProps {
  selectedMeasure: MeasureMetadata | null;
  measureData: Record<string, any>;
  targets: TransformedTarget[];
  workPeriods: WorkPeriod[];
  showAverageLine: boolean;
  handleMeasureClick: (measure: string, workPeriodName: string, title: string, date?: string | null) => void;
}

export const MeasuresOverTimeChart = ({
  selectedMeasure,
  measureData,
  workPeriods,
  showAverageLine,
  handleMeasureClick,
}: MeasuresOverTimeChartProps) => {
  const chartRef = useRef<HTMLDivElement>(null!);

  // Sort work periods by start date (oldest to newest)
  const sortedWorkPeriods = useMemo(() => sortWorkPeriodsByStartDate(workPeriods), [workPeriods]);

  // Process chart data
  const chartData = useMemo(() => {
    const baseChartData = transformMeasureData(measureData, sortedWorkPeriods, selectedMeasure?.measure_name || '');

    // Add average line if enabled
    if (showAverageLine && selectedMeasure) {
      return baseChartData.map((dataPoint) => {
        // Calculate average for this time point across all work periods
        const values: number[] = [];
        sortedWorkPeriods.forEach((workPeriod) => {
          const value = dataPoint[workPeriod.id];
          if (typeof value === 'number') {
            values.push(value);
          }
        });

        const average = values.length > 0 ? values.reduce((sum, val) => sum + val, 0) / values.length : null;

        return {
          ...dataPoint,
          average: average,
        };
      });
    }

    return baseChartData;
  }, [measureData, selectedMeasure, sortedWorkPeriods, showAverageLine]);

  const trendDatasets = useMemo(
    () => buildTrendDatasets(chartData as any, sortedWorkPeriods),
    [chartData, sortedWorkPeriods],
  );

  return (
    <ChartWrapper>
      {chartData.length > 0 ? (
        <ChartContent ref={chartRef}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData as Record<string, string | number>[]}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dayKey" type="category" allowDuplicatedCategory={false} />
              <YAxis
                domain={calculateYDomain(chartData, sortedWorkPeriods, showAverageLine, trendDatasets)}
                tickFormatter={(value) => Math.round(value).toString()}
              />
              <Tooltip content={<CustomTooltip selectedMeasure={selectedMeasure} />} />
              <Legend />

              {/* Average line */}
              {showAverageLine && (
                <Line
                  type="monotone"
                  dataKey="average"
                  name="Average"
                  stroke={newCOLORS.darkGray2}
                  strokeDasharray="5 5"
                  strokeWidth={3}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              )}

              {sortedWorkPeriods.map((workPeriod, index) => {
                const color = DEFAULT_COLORS[index % DEFAULT_COLORS.length];
                return (
                  <Fragment key={workPeriod.id}>
                    {/* Regular data line */}
                    <Line
                      type="monotone"
                      dataKey={workPeriod.id}
                      name={workPeriod.name}
                      stroke={color}
                      activeDot={(props: any) => {
                        const { cx, cy, index } = props;
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={4}
                            fill={color}
                            stroke={color}
                            strokeWidth={2}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              // Given the index of the point and the start/end date of the sprint (workPeriod), find the date in YYYY-MM-DD format
                              // Assume workPeriod has start_date and end_date in ISO format (YYYY-MM-DD)
                              let clickedDate: string | null = null;
                              if (workPeriod.start_date && workPeriod.end_date && typeof index === 'number') {
                                const startDate = new Date(workPeriod.start_date);
                                // Add 'index' days to startDate
                                const resultDate = new Date(startDate);
                                resultDate.setDate(startDate.getDate() + index);
                                // Format as YYYY-MM-DD
                                const year = resultDate.getFullYear();
                                const month = String(resultDate.getMonth() + 1).padStart(2, '0');
                                const day = String(resultDate.getDate()).padStart(2, '0');
                                clickedDate = `${year}-${month}-${day}`;
                              }
                              if (typeof index === 'number' && selectedMeasure) {
                                handleMeasureClick(
                                  selectedMeasure.measure_name,
                                  workPeriod.name,
                                  selectedMeasure.measure_title,
                                  clickedDate,
                                );
                              }
                            }}
                          />
                        );
                      }}
                      connectNulls
                      isAnimationActive={false}
                    />
                  </Fragment>
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </ChartContent>
      ) : (
        <EmptyState>
          <Text>Select a measure and work periods to view the chart.</Text>
        </EmptyState>
      )}
    </ChartWrapper>
  );
};

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const ChartContent = styled.div`
  flex: 1;
  min-height: 300px;
  width: 100%;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  text-align: center;
`;
